import { z } from 'zod';

import {
  additionalClosingOperationValues,
  ReviewedRequest,
  ReviewedRequestAction,
  ReviewedRequestState,
  UserActionRequestData,
} from '@Common/types/bo/reviewed_requests';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import { ZodDateTimeString } from '@Common/types/zod/zod-date-string';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export interface PostUserActionRequest {
  user_ids: number[];
  data: UserActionRequestData;
  reason: string;
}

export interface PostUserActionResponse {
  id: number;
  state: ReviewedRequestState;
  created_at: Date;
}

export type PutUserActionRequest = {
  state: ReviewedRequestAction;
};

export type PutUserActionResponse = ReviewedRequest;

export const RestrictionsFlagsValues = [
  'suspended',
  'block_money_actions',
  'block_deposit_actions',
  'block_withdrawal_actions',
  'closed',
  'closing',
  'social_ban',
] as const;
export const ZodRestrictionsFlag = z.enum(RestrictionsFlagsValues);
export const ZodRestrictionsFlags = z.array(ZodRestrictionsFlag);

export type RestrictionsFlag = z.infer<typeof ZodRestrictionsFlag>;

export const ZodUserActionRequestData = z.object({
  restrictions_flags: ZodRestrictionsFlags.optional(),
  from_restrictions_flags: ZodRestrictionsFlags.optional(),
  reopen_account: z.boolean().optional(),
  is_ambassador: z.boolean().optional(),
  leave_on_account_after_transfer: z.number().int().optional(),
  additional_closing_operation: z.enum(additionalClosingOperationValues).optional(),
  manual_poi: z.boolean().optional(),
  manual_poa: z.boolean().optional(),
  manual_sof: z.boolean().optional(),
});

const ZodPostUserActionRequest = z.object({
  user_ids: z.array(z.number().int()).max(50),
  data: ZodUserActionRequestData,
  reason: ZodTrimmedString,
});

const ZodPostUserActionResponse = z.object({
  id: ZodId,
  state: z.enum(['REQUESTED']),
  created_at: z.date(),
});

const ZodPutUserActionRequest = z.object({
  state: z.enum(['APPROVED', 'REJECTED']),
});

export const ZodGetUserActionResponse = z.object({
  id: ZodId,
  user_ids: z.array(z.number().int()).length(1),
  requester_id: ZodId,
  requester_email: ZodTrimmedString,
  data: ZodUserActionRequestData,
  reason: ZodTrimmedString,
  state: z.enum(['REQUESTED', 'APPROVED', 'REJECTED', 'APPROVED_WITH_ERROR']),
  created_at: ZodDateTimeString,
  updated_at: ZodDateTimeString,
  reviewer_id: ZodId.nullable(),
  reviewer_email: ZodTrimmedString.nullable(),
  reviewed_at: ZodDateTimeString.nullable(),
  type: ZodTrimmedString,
  error_message: ZodTrimmedString.nullable(),
  mdlr_payment_ids: z.array(ZodTrimmedString),
  shares_ca_id: ZodId.nullable(),
  assignee_id: ZodId.nullable(),
  assignee_email: ZodTrimmedString.nullable(),
});

const ZodPutUserActionResponse = ZodGetUserActionResponse;

export const postUserActionRequestsEndpoint = endpoint({
  method: 'POST',
  body: ZodPostUserActionRequest,
  responseBody: ZodPostUserActionResponse,
});

export type PostUserActionRequestsEndpoint = GetEndpointType<typeof postUserActionRequestsEndpoint>;

export const putUserActionRequestsEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutUserActionRequest,
  responseBody: ZodPutUserActionResponse,
});

export type PutUserActionRequestsEndpoint = GetEndpointType<typeof putUserActionRequestsEndpoint>;
